import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Target,
  Search,
  TrendingUp,
  Sparkles,
  BarChart3,
  Zap,
} from "lucide-react";
import { Section, Eyebrow, FadeIn } from "@/components/Section";
import useSEO from "@/hooks/useSEO";

const HERO_IMG =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZXh0dXJlfGVufDB8fHx8MTc4MDkyNTQ3M3ww&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: Target,
    title: "Meta Ads",
    blurb:
      "Facebook & Instagram campaigns engineered around your CAC, ROAS and LTV — not vanity metrics.",
    bullets: ["Creative testing", "CAPI / pixel", "Funnel scaling"],
    span: "md:col-span-7",
  },
  {
    icon: Search,
    title: "Google Ads",
    blurb:
      "Search, Performance Max, YouTube. Built for high-intent capture and predictable pipeline growth.",
    bullets: ["Search & PMax", "Shopping feeds", "GA4 + Looker"],
    span: "md:col-span-5",
  },
  {
    icon: TrendingUp,
    title: "SEO",
    blurb:
      "Technical, content & link strategy to win category share — compounding traffic, not one-off spikes.",
    bullets: ["Tech audit", "Topical authority", "EEAT content"],
    span: "md:col-span-5",
  },
  {
    icon: Sparkles,
    title: "Creative",
    blurb:
      "Ad creative production tied directly to your performance feedback loop.",
    bullets: ["UGC + static", "Motion ads", "Hook frameworks"],
    span: "md:col-span-4",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    blurb: "Server-side tracking, attribution and dashboards your CFO trusts.",
    bullets: ["GA4 / GTM", "Server tagging", "Looker reports"],
    span: "md:col-span-3",
  },
];

const metrics = [
  { k: "€18M+", v: "ad spend managed" },
  { k: "4.7×", v: "avg blended ROAS" },
  { k: "120+", v: "campaigns shipped" },
  { k: "11", v: "countries served" },
  { k: "94%", v: "client retention" },
];

export default function Home() {
  useSEO({
    title: null,
    description:
      "Bright Solutions Europe is a Warsaw-based performance marketing agency. We scale European brands with Meta Ads, Google Ads and SEO — measured by revenue, not impressions.",
    path: "/",
  });
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero"
        className="relative min-h-[100svh] flex items-end overflow-hidden pt-24"
      >
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black via-black/40 to-transparent" />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 md:pb-32 w-full">
          <FadeIn>
            <Eyebrow dataTestid="hero-eyebrow">
              Warsaw · Performance marketing across Europe
            </Eyebrow>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1
              data-testid="hero-title"
              className="font-display mt-6 text-5xl sm:text-7xl lg:text-[8.5rem] font-black tracking-tighter leading-[0.88] max-w-5xl"
            >
              We turn ad
              <br />
              spend into
              <br />
              <span className="text-[#ccff00]">revenue.</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
              <p className="md:col-span-5 text-zinc-300 text-lg leading-relaxed max-w-md">
                Bright Solutions Europe is a Warsaw-based growth agency for
                ambitious brands. Meta Ads · Google Ads · SEO — built around
                profit, not pixels.
              </p>
              <div className="md:col-span-7 md:justify-self-end flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 bg-[#ccff00] hover:bg-[#d4ff33] text-black px-6 py-4 font-bold tracking-tight transition"
                >
                  Get a free audit
                  <ArrowUpRight className="h-5 w-5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/case-studies"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-6 py-4 font-bold tracking-tight transition"
                >
                  See results
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Floating metric badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="hidden lg:flex absolute top-32 right-10 flex-col gap-1 border border-white/10 bg-black/60 backdrop-blur-xl px-5 py-4"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Avg ROAS
          </span>
          <span className="font-display text-3xl font-black text-[#ccff00]">
            4.7×
          </span>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div
        data-testid="metrics-marquee"
        className="relative border-y border-white/10 bg-black/80 overflow-hidden"
      >
        <div className="marquee-track flex whitespace-nowrap py-6">
          {[...metrics, ...metrics, ...metrics].map((m, i) => (
            <div key={i} className="flex items-center gap-3 mx-10">
              <span className="font-display text-2xl font-black text-white">
                {m.k}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                {m.v}
              </span>
              <span className="text-[#ccff00] mx-6">×</span>
            </div>
          ))}
        </div>
      </div>

      {/* SERVICES BENTO */}
      <Section dataTestid="home-services">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12">
          <div className="md:col-span-7">
            <Eyebrow>What we do</Eyebrow>
            <h2 className="font-display mt-4 text-4xl sm:text-6xl font-black tracking-tighter leading-[0.95]">
              Three channels.
              <br />
              One <span className="text-[#ccff00]">revenue engine.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-zinc-400 leading-relaxed">
            We're channel-agnostic about tactics and obsessive about outcomes.
            Every campaign is tied to a unit economics model your CFO would
            sign off on.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-fr">
          {services.map((s, i) => (
            <FadeIn
              key={s.title}
              delay={i * 0.06}
              className={`${s.span} group`}
            >
              <div className="h-full bg-[#0f0f10] border border-white/10 hover:border-[#ccff00] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_0_1px_#ccff00,0_30px_80px_-30px_rgba(204,255,0,0.35)]">
                <div className="flex items-start justify-between mb-8">
                  <s.icon className="h-7 w-7 text-[#ccff00]" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
                  {s.blurb}
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-300 border border-white/10 px-2.5 py-1"
                    >
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/services"
            data-testid="home-services-cta"
            className="inline-flex items-center gap-2 text-white hover:text-[#ccff00] transition group font-medium"
          >
            Explore all services
            <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Section>

      {/* PROCESS */}
      <Section
        dataTestid="home-process"
        className="border-t border-white/10 bg-[#0a0a0b]"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Eyebrow>Process</Eyebrow>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              No fluff.
              <br />
              <span className="text-[#ccff00]">A system.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Every engagement follows the same playbook — refined over 120+
              campaigns and €18M of ad spend.
            </p>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
            {[
              {
                n: "01",
                t: "Diagnose",
                d: "Free audit of your ad accounts, GA4, funnel and creative.",
              },
              {
                n: "02",
                t: "Plan",
                d: "Channel mix, budget allocation and a 90-day growth roadmap.",
              },
              {
                n: "03",
                t: "Ship",
                d: "Campaigns, landing pages, creative & tracking — live in 14 days.",
              },
              {
                n: "04",
                t: "Scale",
                d: "Weekly testing cadence. Monthly QBR. Always linked to revenue.",
              },
            ].map((p) => (
              <div
                key={p.n}
                className="bg-[#0f0f10] p-8 flex flex-col gap-3 hover:bg-[#141416] transition"
              >
                <div className="flex items-center justify-between">
                  <Zap className="h-5 w-5 text-[#ccff00]" strokeWidth={1.5} />
                  <span className="font-mono text-xs text-zinc-500">{p.n}</span>
                </div>
                <h4 className="font-display text-xl font-bold tracking-tight">
                  {p.t}
                </h4>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section
        dataTestid="home-final-cta"
        className="border-t border-white/10"
      >
        <FadeIn>
          <div className="bg-[#ccff00] text-black p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em]">
                Free 30-min growth audit
              </p>
              <h2 className="font-display mt-4 text-4xl sm:text-6xl font-black tracking-tighter leading-[0.92]">
                We'll find €10k+ of
                <br />
                hidden growth in your
                <br />
                accounts. Or you walk.
              </h2>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <Link
                to="/contact"
                data-testid="home-final-cta-button"
                className="inline-flex items-center gap-2 bg-black hover:bg-zinc-900 text-white px-6 py-4 font-bold tracking-tight transition group"
              >
                Claim your audit
                <ArrowUpRight className="h-5 w-5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
}
